import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { graphql, useStaticQuery } from "gatsby"
import {
  CONSENT_KEY,
  DENIED,
  readConsent,
  refreshConsent,
  subscribeConsentChanges,
  saveConsent,
  preserveConsentRecord,
  restoreConsentFocus,
} from "./consent.js"
import { applyTrackingConsent, configureTracking } from "./tracking.js"
import ConsentBanner from "./ConsentBanner"
import "./ConsentBanner.css"

const ConsentContext = createContext(null)

export function ConsentProvider({ children }) {
  const { site } = useStaticQuery(graphql`
    query TrinityConsentConfiguration {
      site {
        siteMetadata {
          tracking {
            gaId
          }
        }
      }
    }
  `)
  const [record, setRecord] = useState(null)
  const [ready, setReady] = useState(false)
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const [error, setError] = useState("")
  const opener = useRef(null)
  const restoreFocus = useRef(false)

  const updateConsent = useCallback(current => {
    setRecord(previous => preserveConsentRecord(previous, current.record))
    setError(
      current.available
        ? ""
        : "Не можем да запазим избора Ви в този браузър. Незадължителните услуги остават изключени."
    )
    setReady(true)
  }, [])

  const sync = useCallback(() => {
    const current = refreshConsent()
    applyTrackingConsent(current.record || DENIED)
    return current
  }, [])

  useEffect(() => {
    configureTracking(site.siteMetadata.tracking)
    const unsubscribe = subscribeConsentChanges(updateConsent)
    sync()
    const onStorage = event => {
      if (event.key === CONSENT_KEY || event.key === null) sync()
    }
    const onVisible = () => {
      if (document.visibilityState === "visible") sync()
    }
    window.addEventListener("storage", onStorage)
    window.addEventListener("focus", sync)
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      unsubscribe()
      window.removeEventListener("storage", onStorage)
      window.removeEventListener("focus", sync)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [site.siteMetadata.tracking, sync, updateConsent])

  useEffect(() => {
    if (!record) return undefined
    let cancelled = false
    let timer
    const schedule = currentRecord => {
      if (cancelled || !currentRecord) return
      const remaining = Date.parse(currentRecord.expiresAt) - Date.now()
      timer = window.setTimeout(() => {
        if (cancelled) return
        // Rearm explicitly even when sync preserves the record's identity.
        const current = sync()
        schedule(current.record)
      }, Math.max(0, Math.min(remaining, 86400000)))
    }
    schedule(record)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [record, sync])

  const closePreferences = () => {
    restoreFocus.current = true
    setPreferencesOpen(false)
  }

  useEffect(() => {
    if (!preferencesOpen && restoreFocus.current) {
      restoreFocus.current = false
      restoreConsentFocus(opener.current)
    }
  }, [preferencesOpen, record])

  const choose = choices => {
    const saved = saveConsent(choices)
    setRecord(saved)
    // Fail closed even when an older grant was active or a script is in flight.
    applyTrackingConsent(saved || DENIED)
    if (!saved) {
      setError(
        "Изборът не може да бъде запазен. Незадължителните услуги остават изключени. Разрешете съхранението за сайта и опитайте отново."
      )
      return
    }
    setError("")
    closePreferences()
  }

  const openPreferences = event => {
    opener.current = event?.currentTarget || null
    setPreferencesOpen(true)
  }

  const value = {
    consent: record || DENIED,
    preferencesOpen,
    openPreferences,
    grantExternalContent: () =>
      choose({ ...(readConsent().record || DENIED), externalContent: true }),
  }

  return (
    <ConsentContext.Provider value={value}>
      {children}
      {ready && (!record || preferencesOpen || error) && (
        <ConsentBanner
          consent={record || DENIED}
          preferencesOpen={preferencesOpen}
          openPreferences={openPreferences}
          closePreferences={closePreferences}
          choose={choose}
          error={error}
        />
      )}
    </ConsentContext.Provider>
  )
}

export const useConsent = () => useContext(ConsentContext)
